import { apiClient } from "./client";

// ─── job types + their checklist templates ───────────────────────────────────

export async function fetchJobTypes(params = {}) {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
  ).toString();
  const res = await apiClient("GET", `/job-types${query ? `?${query}` : ""}`);
  return res.data;
}

export async function createJobType(payload) {
  const res = await apiClient("POST", "/job-types", payload);
  return res.data;
}

export async function updateJobType(id, payload) {
  const res = await apiClient("PATCH", `/job-types/${id}`, payload);
  return res.data;
}

// Retires the type: deactivated when jobs still use it, deleted when none do.
export async function deleteJobType(id) {
  const res = await apiClient("DELETE", `/job-types/${id}`);
  return res.data;
}

export async function fetchJobTypeChecklist(id) {
  const res = await apiClient("GET", `/job-types/${id}/checklist`);
  return res.data;
}

export async function saveJobTypeChecklist(id, payload) {
  const res = await apiClient("PUT", `/job-types/${id}/checklist`, payload);
  return res.data;
}

// The template for a job type *by name* — what the job form seeds from, since
// jobs store the type as a string (see the job-type-by-name invariant). A visit
// seeds from its visit type instead, by id.
export async function fetchTemplateForJobType(name) {
  const res = await apiClient("GET", `/job-types/template?name=${encodeURIComponent(name)}`);
  return res.data;
}

// ─── visit types + their checklist templates ─────────────────────────────────
//
// A visit references its type by id, so its template is fetched by id too —
// there is no by-name `/template` lookup like job types need.

export async function fetchVisitTypes(params = {}) {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
  ).toString();
  const res = await apiClient("GET", `/visit-types${query ? `?${query}` : ""}`);
  return res.data;
}

export async function createVisitType(payload) {
  const res = await apiClient("POST", "/visit-types", payload);
  return res.data;
}

export async function updateVisitType(id, payload) {
  const res = await apiClient("PATCH", `/visit-types/${id}`, payload);
  return res.data;
}

// Retires the type: deactivated when visits still use it, deleted when none do.
export async function deleteVisitType(id) {
  const res = await apiClient("DELETE", `/visit-types/${id}`);
  return res.data;
}

// → { visit_type, visit: [...], visit_closure: [...] }. Also what the visit
// form seeds a new visit from, which is why the backend reads it at
// visits.read rather than the write permission.
export async function fetchVisitTypeChecklist(id) {
  const res = await apiClient("GET", `/visit-types/${id}/checklist`);
  return res.data;
}

// `payload` is `{ visit: [...], visit_closure: [...] }` — a scope key omitted
// leaves that scope's template alone (see replace_visit_type_checklist).
export async function saveVisitTypeChecklist(id, payload) {
  const res = await apiClient("PUT", `/visit-types/${id}/checklist`, payload);
  return res.data;
}

// ─── PO types + their checklist templates ────────────────────────────────────
//
// Flat single scope like visit types (SCOPE_PO, not the job type's 2x2), but
// referenced by NAME like job types: purchase_orders.po_type is the legacy
// BuildOps string the periodic refresh keeps writing.

export async function fetchPOTypes(params = {}) {
  const query = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ""))
  ).toString();
  const res = await apiClient("GET", `/po-types${query ? `?${query}` : ""}`);
  return res.data;
}

export async function createPOType(payload) {
  const res = await apiClient("POST", "/po-types", payload);
  return res.data;
}

export async function updatePOType(id, payload) {
  const res = await apiClient("PATCH", `/po-types/${id}`, payload);
  return res.data;
}

// Retires the type: deactivated when POs still use it, deleted when none do.
export async function deletePOType(id) {
  const res = await apiClient("DELETE", `/po-types/${id}`);
  return res.data;
}

// → { po_type, po: [...] }. Also what a new PO of that type seeds from, which
// is why the backend reads it at purchase_orders.read rather than the write
// permission.
export async function fetchPOTypeChecklist(id) {
  const res = await apiClient("GET", `/po-types/${id}/checklist`);
  return res.data;
}

export async function savePOTypeChecklist(id, items) {
  const res = await apiClient("PUT", `/po-types/${id}/checklist`, { po: items });
  return res.data;
}

// ─── departments ─────────────────────────────────────────────────────────────

export async function fetchDepartments() {
  const res = await apiClient("GET", "/departments");
  return res.data;
}

export async function createDepartment(payload) {
  const res = await apiClient("POST", "/departments", payload);
  return res.data;
}

export async function updateDepartment(id, payload) {
  const res = await apiClient("PATCH", `/departments/${id}`, payload);
  return res.data;
}

export async function setDepartmentMembers(id, memberIds) {
  const res = await apiClient("PUT", `/departments/${id}/members`, { member_ids: memberIds });
  return res.data;
}

export async function deleteDepartment(id) {
  const res = await apiClient("DELETE", `/departments/${id}`);
  return res.data;
}

// ─── a job's / a visit's checklist ───────────────────────────────────────────

export async function fetchJobChecklist(jobId) {
  const res = await apiClient("GET", `/jobs/${jobId}/checklist`);
  return res.data;
}

// Pre-job + per-job scopes only. There is no `propagate` flag any more: a
// visit's tasks come from its visit type, so a job has nothing to fan out.
export async function saveJobChecklist(jobId, payload) {
  const res = await apiClient("PUT", `/jobs/${jobId}/checklist`, payload);
  return res.data;
}

// Pulls in template tasks added since the job was created (templates otherwise
// snapshot at creation and never rewrite an existing checklist).
export async function syncJobChecklist(jobId) {
  const res = await apiClient("POST", `/jobs/${jobId}/checklist/sync`);
  return res.data;
}

// → { visit: [...], visit_closure: [...] }.
export async function fetchVisitChecklist(visitId) {
  const res = await apiClient("GET", `/visits/${visitId}/checklist`);
  return res.data;
}

// `payload` is `{ visit: [...], visit_closure: [...] }` — a scope key omitted
// leaves that scope's list alone (see replace_visit_checklist).
export async function saveVisitChecklist(visitId, payload) {
  const res = await apiClient("PUT", `/visits/${visitId}/checklist`, payload);
  return res.data;
}

// Pulls in template tasks added since the visit was created / its type was
// last re-selected (templates otherwise snapshot and never rewrite an
// existing checklist) — the visit equivalent of syncJobChecklist above.
export async function syncVisitChecklist(visitId) {
  const res = await apiClient("POST", `/visits/${visitId}/checklist/sync`);
  return res.data;
}

export async function fetchPOChecklist(orderId) {
  const res = await apiClient("GET", `/purchase_orders/${orderId}/checklist`);
  return res.data;
}

export async function savePOChecklist(orderId, items) {
  const res = await apiClient("PUT", `/purchase_orders/${orderId}/checklist`, { po: items });
  return res.data;
}

// ─── completion + proof ──────────────────────────────────────────────────────

export async function completeChecklistItem(itemId) {
  const res = await apiClient("POST", `/checklist-items/${itemId}/complete`);
  return res.data;
}

export async function uncompleteChecklistItem(itemId) {
  const res = await apiClient("POST", `/checklist-items/${itemId}/uncomplete`);
  return res.data;
}

// Only meaningful on an item carrying `status_options`. Passing `complete`
// checks the box too (same proof check + visit/job advance as the checkbox);
// `status: null` clears it back to unset. Returns the updated item.
export async function setChecklistItemStatus(itemId, status) {
  const res = await apiClient("POST", `/checklist-items/${itemId}/status`, { status });
  return res.data;
}

// Take a department-routed task onto your own My Tasks group: the assignee
// becomes the caller and the department is cleared. Returns the updated item.
export async function claimChecklistItem(itemId) {
  const res = await apiClient("POST", `/checklist-items/${itemId}/claim`);
  return res.data;
}

// kind: "photo" | "attachment". Returns the updated item (with its proof list).
// 5 minutes: enough for a 50 MB file (check_upload's cap) on a weak signal,
// but a lost response still ends instead of leaving "Working…" up forever.
export const PROOF_UPLOAD_TIMEOUT_MS = 300_000;

export async function uploadChecklistProof(itemId, kind, file, { onProgress } = {}) {
  const form = new FormData();
  form.append("file", file);
  const res = await apiClient("POST", `/checklist-items/${itemId}/${kind}`, form, {
    timeout: PROOF_UPLOAD_TIMEOUT_MS,
    onUploadProgress: onProgress
      ? (e) => {
          if (e.total) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      : undefined,
  });
  return res.data;
}

// Change a single task's department/assignee routing in place, without
// opening the full checklist editor. Management-tier only. Returns the
// updated item.
export async function updateChecklistItemRouting(itemId, { department_id, department_role, assigned_user_id, route_to_project_manager } = {}) {
  const res = await apiClient("PATCH", `/checklist-items/${itemId}/routing`, {
    department_id, department_role, assigned_user_id, route_to_project_manager,
  });
  return res.data;
}

// Edit a single task's details text in place. Management-tier only; blank
// clears it. Returns the updated item.
export async function updateChecklistItemDetails(itemId, details) {
  const res = await apiClient("PATCH", `/checklist-items/${itemId}/details`, { details });
  return res.data;
}

// Remove a single task without opening the full checklist editor.
export async function deleteChecklistItem(itemId) {
  const res = await apiClient("DELETE", `/checklist-items/${itemId}`);
  return res.data;
}

// Bulk "Sync from template" for visits selected on a job (add-only, per visit).
// → { added_total, synced: [{visit_id, added}], skipped: [{visit_id, reason}] }
export async function syncVisitChecklists(jobId, visitIds) {
  const res = await apiClient("POST", `/jobs/${jobId}/visits/checklist/sync`, { visit_ids: visitIds });
  return res.data;
}
